import { useEffect, useState } from "react";
import api from "@/api/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { useSiteContent } from "@/context/SiteContentContext";
import { Radio, Megaphone, Trash2 } from "lucide-react";
import CountdownPill from "@/components/CountdownTimer";

function BatchManager() {
  const [batches, setBatches] = useState([]);
  const [hours, setHours] = useState({});
  const [openBatch, setOpenBatch] = useState(null);

  const load = async () => {
    try { const { data } = await api.get("/admin/live-batches"); setBatches(data || []); }
    catch { toast.error("Could not load live batches"); }
  };
  useEffect(() => { load(); }, []);

  const saveHours = async (b) => {
    const h = Number(hours[b.id]);
    if (!h || h < 48 || h > 240) return toast.error("Duration must be 48–240 hours");
    try { await api.patch(`/admin/live-batches/${b.id}`, { duration_hours: h }); toast.success("Batch duration updated"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Update failed"); }
  };
  const deactivate = async (b) => {
    if (!window.confirm(`Deactivate ${b.vendor_business_name}'s batch? Its products leave the storefront immediately.`)) return;
    try { await api.post(`/admin/live-batches/${b.id}/deactivate`); toast.success("Batch deactivated"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const removeBatch = async (b) => {
    if (!window.confirm(`Remove ${b.vendor_business_name}'s batch entirely? Products are unpublished.`)) return;
    try { await api.delete(`/admin/live-batches/${b.id}`); toast.success("Batch removed"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const removeProduct = async (b, p) => {
    if (!window.confirm(`Remove "${p.name}" from this live batch?`)) return;
    try { await api.delete(`/admin/live-batches/${b.id}/products/${p.id}`); toast.success("Product removed from batch"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const pill = { draft: "bg-gray-100 text-gray-600 border-gray-200", live: "bg-green-50 text-green-700 border-green-200", expired: "bg-gray-100 text-gray-400 border-gray-200" };

  return (
    <div className="p-5 bg-white border border-gray-200 rounded-2xl" data-testid="admin-batch-manager">
      <div className="font-display text-xl mb-1">Vendor live batches</div>
      <p className="text-xs text-gray-500 mb-4">Every batch shares one timer. Extend/reduce a live batch's duration, remove individual products, or deactivate/remove whole batches. Edit any product's details from the Products tab.</p>
      {batches.length === 0 ? (
        <div className="text-sm text-gray-400 py-6 text-center border border-dashed border-gray-200 rounded-xl">No live batches yet.</div>
      ) : (
        <div className="space-y-3">
          {batches.map((b) => (
            <div key={b.id} data-testid={`admin-batch-${b.id}`} className="p-4 border border-gray-200 rounded-2xl">
              <div className="flex flex-wrap items-center gap-3 justify-between">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium text-sm">{b.vendor_business_name}</span>
                  <span className={`text-[10px] uppercase tracking-widest border px-2 py-0.5 rounded-full ${pill[b.status] || pill.draft}`} data-testid={`admin-batch-status-${b.id}`}>{b.status}</span>
                  <span className="text-xs text-gray-500">{b.product_count} products{b.status === "live" ? ` · ${b.active_product_count} active` : ""}</span>
                  {b.starts_at && <span className="text-[11px] text-gray-400">Start {new Date(b.starts_at).toLocaleString("en-IN")} · Expiry {new Date(b.expires_at).toLocaleString("en-IN")}</span>}
                </div>
                {b.status === "live" && <CountdownPill targetIso={b.expires_at} />}
              </div>
              <div className="flex items-center gap-2 flex-wrap mt-3">
                <Button data-testid={`admin-batch-products-${b.id}`} size="sm" variant="outline" className="h-8 rounded-full text-xs" onClick={() => setOpenBatch(openBatch === b.id ? null : b.id)}>
                  {openBatch === b.id ? "Hide products" : "Products"}
                </Button>
                {b.status === "live" && (
                  <div className="flex items-center gap-1">
                    <Input data-testid={`admin-batch-hours-${b.id}`} type="number" min="48" max="240" value={hours[b.id] ?? b.duration_hours ?? 48} onChange={(e) => setHours({ ...hours, [b.id]: e.target.value })} className="h-8 w-20 text-xs" />
                    <Button data-testid={`admin-batch-hours-save-${b.id}`} size="sm" variant="outline" className="h-8 rounded-full text-xs" onClick={() => saveHours(b)}>Save hrs</Button>
                  </div>
                )}
                {b.status !== "expired" && (
                  <Button data-testid={`admin-batch-deactivate-${b.id}`} size="sm" variant="outline" className="h-8 rounded-full text-xs text-amber-700 border-amber-200 hover:bg-amber-50" onClick={() => deactivate(b)}>Deactivate</Button>
                )}
                <Button data-testid={`admin-batch-remove-${b.id}`} size="sm" variant="outline" className="h-8 rounded-full text-xs text-red-600 border-red-200 hover:bg-red-50" onClick={() => removeBatch(b)}><Trash2 className="h-3.5 w-3.5" /></Button>
              </div>
              {openBatch === b.id && (
                <div className="mt-3 space-y-1.5" data-testid={`admin-batch-product-list-${b.id}`}>
                  {(b.products || []).map((p) => (
                    <div key={p.id} className="flex items-center gap-3 text-xs p-2 rounded-lg bg-gray-50">
                      <span className="flex-1 truncate font-medium">{p.name}</span>
                      <span className="text-gray-500">₹{Number(p.price).toLocaleString("en-IN")} · stock {p.stock}{!p.visible ? " · hidden" : ""}</span>
                      {b.status === "live" && (
                        <button data-testid={`admin-batch-rm-prod-${p.id}`} onClick={() => removeProduct(b, p)} className="text-red-500 hover:underline">Remove from batch</button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const saveKey = (key, value) => api.patch(`/admin/site-content/${encodeURIComponent(key)}`, { value: String(value) });

const readFile = (file, maxW = 1600) => new Promise((resolve, reject) => {
  const img = new Image();
  const url = URL.createObjectURL(file);
  img.onload = () => {
    const scale = Math.min(1, maxW / img.width);
    const canvas = document.createElement("canvas");
    canvas.width = Math.round(img.width * scale);
    canvas.height = Math.round(img.height * scale);
    canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
    URL.revokeObjectURL(url);
    resolve(canvas.toDataURL("image/jpeg", 0.82));
  };
  img.onerror = reject;
  img.src = url;
});

function ContentField({ label, k, value, onChange, textarea }) {
  return (
    <div>
      <Label className="text-xs">{label}</Label>
      {textarea
        ? <textarea data-testid={`live-admin-${k}`} value={value} onChange={(e) => onChange(e.target.value)} rows={2} className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm" />
        : <Input data-testid={`live-admin-${k}`} value={value} onChange={(e) => onChange(e.target.value)} className="h-9 mt-1" />}
    </div>
  );
}

export default function AdminLiveTab() {
  const { content, refresh } = useSiteContent();
  const [subs, setSubs] = useState([]);
  const [strip, setStrip] = useState({ text: "", bg: "#111827", visible: true });
  const [hero, setHero] = useState({ kicker: "", headline: "", subtext: "", bg: "#FEF3E6", image: "", cta_text: "", cta_link: "", visible: true });
  const [editHours, setEditHours] = useState({});
  const [savingStrip, setSavingStrip] = useState(false);
  const [savingHero, setSavingHero] = useState(false);

  const loadSubs = async () => {
    try { const { data } = await api.get("/admin/live-submissions"); setSubs(data || []); }
    catch { toast.error("Could not load live submissions"); }
  };
  useEffect(() => { loadSubs(); }, []);

  useEffect(() => {
    setStrip({
      text: content["live.announcement.text"] || "",
      bg: content["live.announcement.bg"] || "#111827",
      visible: content["live.announcement.visible"] !== "false",
    });
    setHero({
      kicker: content["live.hero.kicker"] || "",
      headline: content["live.hero.headline"] || "",
      subtext: content["live.hero.subtext"] || "",
      bg: content["live.hero.bg"] || "#FEF3E6",
      image: content["live.hero.image"] || "",
      cta_text: content["live.hero.cta_text"] || "",
      cta_link: content["live.hero.cta_link"] || "",
      visible: content["live.hero.visible"] !== "false",
    });
  }, [content]);

  const saveStrip = async () => {
    setSavingStrip(true);
    try {
      await saveKey("live.announcement.text", strip.text);
      await saveKey("live.announcement.bg", strip.bg);
      await saveKey("live.announcement.visible", strip.visible ? "true" : "false");
      toast.success("Announcement strip saved");
      refresh && refresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save strip"); }
    finally { setSavingStrip(false); }
  };

  const saveHero = async () => {
    setSavingHero(true);
    try {
      for (const k of ["kicker", "headline", "subtext", "bg", "image", "cta_text", "cta_link"]) await saveKey(`live.hero.${k}`, hero[k]);
      await saveKey("live.hero.visible", hero.visible ? "true" : "false");
      toast.success("Live hero banner saved");
      refresh && refresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save hero"); }
    finally { setSavingHero(false); }
  };

  const patch = async (s, body) => {
    try {
      await api.patch(`/admin/live-banners/${s.id}`, body);
      loadSubs();
    } catch (e) { toast.error(e.response?.data?.detail || "Update failed"); }
  };

  const uploadBanner = async (s, file) => {
    if (!file) return;
    try {
      const dataUrl = await readFile(file);
      await patch(s, { banner_image: dataUrl });
      toast.success("Banner image updated");
    } catch { toast.error("Could not read that image"); }
  };

  const remove = async (s) => {
    if (!window.confirm(`Remove ${s.vendor_business_name}'s live banner from the homepage?`)) return;
    try {
      await api.delete(`/admin/live-banners/${s.id}`);
      toast.success("Banner removed");
      loadSubs();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not remove"); }
  };

  return (
    <div className="space-y-6" data-testid="admin-live-tab">
      <div className="p-5 bg-white border border-gray-200 rounded-2xl">
        <div className="flex items-center justify-between mb-1">
          <div className="flex items-center gap-2"><Megaphone className="h-4 w-4 text-[#F48E2E]" /><div className="font-display text-xl">Announcement strip</div></div>
          <div className="flex items-center gap-2 text-xs"><span className="text-gray-500">Visible</span><Switch data-testid="strip-visible" checked={strip.visible} onCheckedChange={(v) => setStrip({ ...strip, visible: v })} /></div>
        </div>
        <p className="text-xs text-gray-500 mb-3">Shown between the main hero and the Live hero. Hidden everywhere when visibility is off.</p>
        <div className="grid sm:grid-cols-[1fr_140px] gap-3">
          <ContentField label="Text" k="strip-text" value={strip.text} onChange={(v) => setStrip({ ...strip, text: v })} />
          <div>
            <Label className="text-xs">Background</Label>
            <div className="flex items-center gap-2 mt-1">
              <input data-testid="strip-bg-color" type="color" value={strip.bg} onChange={(e) => setStrip({ ...strip, bg: e.target.value })} className="h-9 w-12 rounded border border-gray-200 cursor-pointer" />
              <Input data-testid="strip-bg-text" value={strip.bg} onChange={(e) => setStrip({ ...strip, bg: e.target.value })} className="h-9 font-mono text-xs" />
            </div>
          </div>
        </div>
        <Button data-testid="strip-save" onClick={saveStrip} disabled={savingStrip} className="mt-3 rounded-full bg-[#F48E2E] hover:bg-[#D97815] h-9">{savingStrip ? "Saving…" : "Save strip"}</Button>
      </div>

      <div className="p-5 bg-white border border-gray-200 rounded-2xl">
        <div className="flex items-center justify-between mb-1">
          <div className="flex items-center gap-2"><Radio className="h-4 w-4 text-red-600" /><div className="font-display text-xl">Live hero banner</div></div>
          <div className="flex items-center gap-2 text-xs"><span className="text-gray-500">Visible</span><Switch data-testid="hero-visible" checked={hero.visible} onCheckedChange={(v) => setHero({ ...hero, visible: v })} /></div>
        </div>
        <div className="grid sm:grid-cols-2 gap-3 mt-2">
          <ContentField label="Kicker" k="hero-kicker" value={hero.kicker} onChange={(v) => setHero({ ...hero, kicker: v })} />
          <ContentField label="Headline" k="hero-headline" value={hero.headline} onChange={(v) => setHero({ ...hero, headline: v })} />
          <div className="sm:col-span-2"><ContentField label="Subtext" k="hero-subtext" textarea value={hero.subtext} onChange={(v) => setHero({ ...hero, subtext: v })} /></div>
          <ContentField label="CTA button text" k="hero-cta-text" value={hero.cta_text} onChange={(v) => setHero({ ...hero, cta_text: v })} />
          <ContentField label="CTA link" k="hero-cta-link" value={hero.cta_link} onChange={(v) => setHero({ ...hero, cta_link: v })} />
          <div>
            <Label className="text-xs">Background</Label>
            <div className="flex items-center gap-2 mt-1">
              <input data-testid="hero-bg-color" type="color" value={hero.bg} onChange={(e) => setHero({ ...hero, bg: e.target.value })} className="h-9 w-12 rounded border border-gray-200 cursor-pointer" />
              <Input data-testid="hero-bg-text" value={hero.bg} onChange={(e) => setHero({ ...hero, bg: e.target.value })} className="h-9 font-mono text-xs" />
            </div>
          </div>
          <div>
            <Label className="text-xs">Background image</Label>
            <div className="flex items-center gap-2 mt-1">
              <Input data-testid="hero-image-url" value={hero.image.startsWith("data:") ? "(uploaded image)" : hero.image} onChange={(e) => setHero({ ...hero, image: e.target.value })} placeholder="Image URL or upload →" className="h-9 text-xs" />
              <label className="h-9 px-3 rounded-full border border-gray-300 text-xs inline-flex items-center cursor-pointer hover:border-[#F48E2E]" data-testid="hero-image-upload">
                Upload
                <input type="file" accept="image/*" className="hidden" onChange={async (e) => { const f = e.target.files?.[0]; if (!f) return; try { setHero({ ...hero, image: await readFile(f) }); } catch { toast.error("Could not read image"); } }} />
              </label>
            </div>
          </div>
        </div>
        <Button data-testid="hero-save" onClick={saveHero} disabled={savingHero} className="mt-3 rounded-full bg-[#F48E2E] hover:bg-[#D97815] h-9">{savingHero ? "Saving…" : "Save hero"}</Button>
      </div>

      <BatchManager />

      <div className="p-5 bg-white border border-gray-200 rounded-2xl">
        <div className="font-display text-xl mb-1">Vendor live submissions</div>
        <p className="text-xs text-gray-500 mb-4">Upload a banner image per vendor, adjust display duration, and toggle homepage visibility. Expired submissions drop off the homepage automatically. Max 6 live banners.</p>
        {subs.length === 0 ? (
          <div className="text-sm text-gray-400 py-8 text-center border border-dashed border-gray-200 rounded-xl">No live submissions yet.</div>
        ) : (
          <div className="space-y-3">
            {subs.map((s) => (
              <div key={s.id} data-testid={`admin-live-row-${s.id}`} className="p-4 border border-gray-200 rounded-2xl flex flex-wrap items-center gap-4">
                <div className="h-14 w-20 rounded-lg overflow-hidden bg-orange-50 shrink-0">
                  {s.banner_image && <img src={s.banner_image} alt="" className="h-full w-full object-cover" />}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-sm">{s.vendor_business_name}</span>
                    <span className={`text-[10px] uppercase tracking-widest border px-2 py-0.5 rounded-full ${s.status === "active" ? "bg-green-50 text-green-700 border-green-200" : "bg-gray-100 text-gray-500 border-gray-200"}`}>{s.status}</span>
                    {!s.active && <span className="text-[10px] uppercase tracking-widest border px-2 py-0.5 rounded-full bg-gray-100 text-gray-500 border-gray-200">hidden from homepage</span>}
                  </div>
                  <div className="text-xs text-gray-600 mt-1 truncate max-w-[420px]">{(s.product_names || []).join(", ")}</div>
                  <div className="text-[11px] text-gray-500 mt-0.5">Start {new Date(s.starts_at).toLocaleString("en-IN")} · Expiry {new Date(s.expires_at).toLocaleString("en-IN")}</div>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <label className="h-8 px-3 rounded-full border border-gray-300 text-xs inline-flex items-center cursor-pointer hover:border-[#F48E2E]" data-testid={`admin-live-upload-${s.id}`}>
                    {s.banner_image ? "Change banner" : "Upload banner"}
                    <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadBanner(s, e.target.files?.[0])} />
                  </label>
                  <div className="flex items-center gap-1">
                    <Input data-testid={`admin-live-hours-${s.id}`} type="number" min="48" max="240" value={editHours[s.id] ?? s.duration_hours} onChange={(e) => setEditHours({ ...editHours, [s.id]: e.target.value })} className="h-8 w-20 text-xs" />
                    <Button data-testid={`admin-live-hours-save-${s.id}`} size="sm" variant="outline" className="h-8 rounded-full text-xs" onClick={() => { const h = Number(editHours[s.id]); if (!h) return toast.error("Enter hours"); patch(s, { duration_hours: h }); }}>Save hrs</Button>
                  </div>
                  <div className="flex items-center gap-1.5 text-xs text-gray-600">
                    <span>Homepage</span>
                    <Switch data-testid={`admin-live-active-${s.id}`} checked={!!s.active} onCheckedChange={(v) => patch(s, { active: v })} />
                  </div>
                  <Button data-testid={`admin-live-remove-${s.id}`} size="sm" variant="outline" className="h-8 rounded-full text-xs text-red-600 border-red-200 hover:bg-red-50" onClick={() => remove(s)}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
